import { useEffect, useId, useRef, useState } from 'react';

export default function UserAccountDialog({ user, isSelf, deleting, onSave, onClose }) {
  const [form, setForm] = useState({ username: user.username || '', email: user.email || '', full_name: user.full_name || '', role: user.role });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false), dialogRef = useRef(null), titleId = useId();
  const close = () => { if (!busyRef.current) onClose(); };
  useEffect(() => {
    const opener = document.activeElement;
    const selectors = 'button:not(:disabled),input:not(:disabled),select:not(:disabled)';
    dialogRef.current?.querySelector(selectors)?.focus();
    const key = event => {
      if (event.key === 'Escape') { event.stopPropagation(); close(); }
      if (event.key !== 'Tab') return;
      const nodes = dialogRef.current?.querySelectorAll(selectors);
      if (!nodes?.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('keydown', key, true); if (opener?.isConnected) opener.focus(); };
  }, []);
  async function submit(event) {
    event.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try { await onSave(deleting ? null : form); onClose(); }
    catch (cause) { setError(cause.message || 'The user could not be updated. Try again.'); }
    finally { busyRef.current = false; setBusy(false); }
  }
  return <div className="o-scrim" onMouseDown={event => event.target === event.currentTarget && close()}>
    <form className="o-modal" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={dialogRef} onSubmit={submit}>
      <div className="o-modal-head"><h2 id={titleId}>{deleting ? 'Delete user' : 'Edit user'}</h2>
        <button type="button" className="o-iconbtn" aria-label="Close user dialog" disabled={busy} onClick={close}>×</button>
      </div>
      <div className="o-modal-body">
        {deleting ? <>
          <p>Delete <strong>{user.full_name || user.username}</strong> ({user.email})?</p>
          <p className="o-note">This removes the user from the active user list and disables sign-in and existing sessions. Their projects, assignments and history are preserved. This does not delete their Microsoft account.</p>
        </> : <>
          {[['username', 'Username', 'text', 100], ['email', 'Email', 'email', 255], ['full_name', 'Full name', 'text', 255]].map(([key, label, type, maxLength]) =>
            <label key={key}><span className="o-field-lbl">{label}</span>
              <input className="o-control" type={type} value={form[key]} maxLength={maxLength} required={key !== 'full_name'} disabled={busy}
                onChange={event => setForm(current => ({ ...current, [key]: event.target.value }))} />
            </label>)}
          <label><span className="o-field-lbl">Platform role</span>
            <select className="o-control" value={form.role} disabled={busy || isSelf}
              onChange={event => setForm(current => ({ ...current, role: event.target.value }))}>
              <option value="user">User</option><option value="admin">Administrator</option>
            </select>
          </label>
          <p className="o-note">{isSelf ? 'Another administrator must change your platform role.' : form.role === 'admin' ? 'Administrators can access every tool and client.' : 'Tool and client access is managed through Access.'}</p>
        </>}
        {error && <p className="o-hint um-account-error" role="alert">{error}</p>}
      </div>
      <div className="o-modal-foot">
        <button type="button" className="o-btn" disabled={busy} onClick={close}>Cancel</button>
        <button type="submit" className={`o-btn ${deleting ? 'o-btn-danger' : 'o-btn-primary'}`} disabled={busy}>
          {busy ? 'Saving...' : deleting ? 'Delete user' : 'Save changes'}
        </button>
      </div>
    </form>
  </div>;
}
